
import {
    Container,
} from 'pixi.js';
import { LoadContext, DisposableGroup } from '../../../utils';
import { Game } from '../../../game';
import { Settings } from '../../../settings';
import { Focusable, UILabel, InteractiveWrapper, Switch } from '../../../ui';
import { RadioButton, RoundedRect } from '../ui';

export class Panel extends Container {

    private bg:RoundedRect;
    protected label:UILabel;
    protected game:Game;
    protected settings:Settings;
    protected icon_x:number = 40;
    protected icon_y:number = 20;
    protected title_vo:string;
    protected explain_vo:string;
    protected settings_vo:string;
    protected disposables: DisposableGroup;

    public static preload() {
        LoadContext.current.add('Settings', './assets/accessibility/Settings{size}.json');
        UILabel.preload();
    }

    constructor( game: Game, title:string, vo:{title:string, explain:string, settings:string}|null, size:{width:number, height:number}, centerLabel = false)
    {
        super();
        this.disposables = new DisposableGroup();
        this.game = game;
        this.settings = this.game.settings;
        this.title_vo = vo?.title;
        this.explain_vo = vo?.explain;
        this.settings_vo = vo?.settings;

        const loader = LoadContext.current;

        this.bg = new RoundedRect( loader.getTexture('whitesquare', 'Settings'), size.width, size.height );
        this.addChild( this.bg );

        if (title)
        {
            this.label = new UILabel(
                title,
                {
                    align: 'center',
                    fontName: 'AsapMedium',
                    fontSize: 33,
                    tint: 0x000000
                },
                loader.getTexture('icon_question', 'Settings')
            );
            this.disposables.add(new InteractiveWrapper(this.label, {
                onHover: async () => {
                    await this.game.vo.playHighlight( this.title_vo );
                },
                onFocus: async () => {
                    await this.game.vo.playHighlight( this.title_vo );
                    await this.game.vo.playHighlight('CYB_Panel_Explain');
                },
                onTap: async () => {
                    this.game.vo.stop();
                    await this.game.vo.playVO( this.explain_vo, true );
                },
                onActivate: async () => {
                    this.game.vo.stop();
                    await this.game.vo.playVO([this.explain_vo, this.settings_vo], true );
                }
            }));
            this.label.position.set( size.width/2 + (centerLabel ? 0 : 30), 40 );
            this.addChild( this.label );
        }
    }

    public get shouldPlayAD()
    {
        return this.settings.playAudioDescriptions.value;
    }

    public getFocusItems():Focusable[]
    {
        let ret:Focusable[] = this.label ? [ this.label ] : [];
        return ret;
    }

    public updateToMatchSetting() {
        // Override this.
    }

    protected async playSwitchHover( theSwitch:Switch, focus = false ) : Promise<void>
    {
        if( theSwitch.isOn ) {
            await this.game.vo.playHighlight( [this.title_vo, 'CYB_Panel1_On'], false );
        } else {
            await this.game.vo.playHighlight( [this.title_vo, 'CYB_Panel1_Off'], false );
        }
        if (focus)
        {
            await this.game.vo.playHighlight('CYB_Panel_SettingEdit');
        }
    }

    /** For playing confirmation VO, not the more interruptable highlight/hover VO */
    protected async playSwitchVO( theSwitch:Switch ) : Promise<void>
    {
        if( theSwitch.isOn ) {
            await this.game.vo.playHighlight( [this.title_vo, 'CYB_Panel1_On'], false );
        } else {
            await this.game.vo.playHighlight( [this.title_vo, 'CYB_Panel1_Off'], false );
        }
    }

    protected async playSwitchSelectionVO( theSwitch:Switch, labelVO:string ) : Promise<void>
    {
        if( theSwitch.isOn ) {
            await this.game.vo.playHighlight( [labelVO, 'CYB_Panel_Selected'], false );
        } else {
            await this.game.vo.playHighlight( [labelVO, 'CYB_Panel_Selected'], false );
        }
    }

    protected async playRadioHover( radioButton:RadioButton, labelVO:string, focus = false ) {
        if( radioButton.active ) {
            await this.game.vo.playHighlight( [labelVO, "CYB_Panel_Selected"], false );
        } else {
            await this.game.vo.playHighlight( labelVO );
        }
        if (focus)
        {
            await this.game.vo.playHighlight('CYB_Panel_SettingEdit');
        }
    }

    /** For playing confirmation VO, not the more interruptable highlight/hover VO */
    protected async playRadioVO( radioButton:RadioButton, labelVO:string ) {
        if( radioButton.active ) {
            await this.game.vo.playVO( [labelVO, "CYB_Panel_Selected"], false );
        } else {
            await this.game.vo.playVO( labelVO );
        }
    }

    destroy()
    {
        this.disposables.dispose();
        super.destroy({children: true});
    }
}